import { Modal, ModalBody, ModalFooter, Button } from "reactstrap";
import { useContext, useEffect, useState } from "react";
import { DocumentTypeContext } from "../../context/DocumentContext";
import { SignerLinkItem } from "../../gateways/DocumentGateway";
import { useToast } from "../../context/ToastContext";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import "./ModalSignerLinks.css";

interface ModalSignerLinksProps {
  openModal: { open: boolean; documentId: string };
  closeModal: () => void;
}

function extractToken(link: string): string {
  const marker = "token=";
  const idx = link.indexOf(marker);
  return idx !== -1 ? link.substring(idx + marker.length) : link;
}

function splitPhone(phone: string): { countryCode: string; number: string } {
  const cleaned = phone.replace(/[\s()-]/g, "");

  if (cleaned.startsWith("+")) {
    const digits = cleaned.substring(1);
    if (digits.startsWith("55") && digits.length >= 12) {
      return { countryCode: "+55", number: digits.substring(2) };
    }
    const cc = digits.substring(0, 2);
    return { countryCode: `+${cc}`, number: digits.substring(2) };
  }

  if (cleaned.startsWith("55") && cleaned.length >= 12) {
    return { countryCode: "+55", number: cleaned.substring(2) };
  }

  return { countryCode: "+55", number: cleaned };
}

function joinPhone(countryCode: string, number: string): string {
  const cc = countryCode.startsWith("+") ? countryCode : `+${countryCode}`;
  const num = number.replace(/\D/g, "");
  return `${cc}${num}`;
}

function formatPhoneDisplay(number: string): string {
  const digits = number.replace(/\D/g, "");
  if (digits.length <= 2) return digits;
  if (digits.length <= 7) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7, 11)}`;
}

type EditField = { index: number; field: "email" | "phone" } | null;

const ModalSignerLinks = ({ openModal, closeModal }: ModalSignerLinksProps) => {
  const { getSignerLinks, resendEmailWithTokens } = useContext(DocumentTypeContext);
  const showToast = useToast();
  const [signers, setSigners] = useState<SignerLinkItem[]>([]);
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);
  const [sending, setSending] = useState(false);

  const [selectedEmail, setSelectedEmail] = useState<Record<number, boolean>>({});
  const [selectedWhatsApp, setSelectedWhatsApp] = useState<Record<number, boolean>>({});

  const [editedEmails, setEditedEmails] = useState<Record<number, string>>({});
  const [editedPhones, setEditedPhones] = useState<Record<number, string>>({});

  const [editing, setEditing] = useState<EditField>(null);
  const [editingValue, setEditingValue] = useState("");
  const [editingCountryCode, setEditingCountryCode] = useState("+55");

  useEffect(() => {
    if (openModal.open && openModal.documentId) {
      getSignerLinks(openModal.documentId).then(setSigners);
      resetState();
    } else {
      setSigners([]);
    }
  }, [openModal.open, openModal.documentId]);

  const resetState = () => {
    setSelectedEmail({});
    setSelectedWhatsApp({});
    setEditedEmails({});
    setEditedPhones({});
    setEditing(null);
    setEditingValue("");
    setEditingCountryCode("+55");
    setCopiedIndex(null);
  };

  const copyToClipboard = async (link: string, index: number) => {
    try {
      await navigator.clipboard.writeText(link);
      setCopiedIndex(index);
      showToast("success", "Link copiado!");
      setTimeout(() => setCopiedIndex(null), 2000);
    } catch {
      showToast("error", "Erro ao copiar link");
    }
  };

  const getEmail = (index: number, signer: SignerLinkItem) =>
    editedEmails[index] ?? signer.email;

  const getPhone = (index: number, signer: SignerLinkItem) =>
    editedPhones[index] ?? signer.phone ?? "";

  const getPhoneDisplay = (index: number, signer: SignerLinkItem) => {
    const phone = getPhone(index, signer);
    if (!phone) return "";
    const { countryCode, number } = splitPhone(phone);
    return `${countryCode} ${formatPhoneDisplay(number)}`;
  };

  const toggleEmail = (index: number) =>
    setSelectedEmail((prev) => ({ ...prev, [index]: !prev[index] }));

  const toggleWhatsApp = (index: number) =>
    setSelectedWhatsApp((prev) => ({ ...prev, [index]: !prev[index] }));

  const startEditing = (index: number, field: "email" | "phone", currentValue: string) => {
    setEditing({ index, field });
    if (field === "phone") {
      const { countryCode, number } = splitPhone(currentValue);
      setEditingCountryCode(countryCode);
      setEditingValue(number);
    } else {
      setEditingValue(currentValue);
    }
  };

  const confirmEdit = () => {
    if (!editing) return;
    if (editing.field === "email") {
      const trimmed = editingValue.trim();
      if (trimmed) setEditedEmails((prev) => ({ ...prev, [editing.index]: trimmed }));
    } else {
      const fullPhone = joinPhone(editingCountryCode, editingValue);
      setEditedPhones((prev) => ({ ...prev, [editing.index]: fullPhone }));
    }
    setEditing(null);
    setEditingValue("");
    setEditingCountryCode("+55");
  };

  const cancelEdit = () => {
    setEditing(null);
    setEditingValue("");
    setEditingCountryCode("+55");
  };

  function getSelectedCount() {
    let count = 0;
    signers.forEach((s, i) => {
      if (s.hasSigned) return;
      if (selectedEmail[i] || selectedWhatsApp[i]) count++;
    });
    return count;
  }

  const selectedCount = getSelectedCount();

  const handleResend = async () => {
    const pending = signers
      .map((s, i) => ({ s, i }))
      .filter(({ s }) => !s.hasSigned);

    const signersToResend = pending
      .filter(({ i }) => (selectedEmail[i] || selectedWhatsApp[i]))
      .filter(({ s }) => s.link)
      .map(({ s, i }) => ({
        email: selectedEmail[i] ? getEmail(i, s) : s.email,
        name: s.name,
        encryptedToken: extractToken(s.link!),
        phone: selectedWhatsApp[i] ? getPhone(i, s) : null,
        sendWhatsApp: !!selectedWhatsApp[i],
      }));

    if (signersToResend.length === 0) {
      showToast("info", "Selecione pelo menos um canal de envio");
      return;
    }

    setSending(true);
    await resendEmailWithTokens(openModal.documentId, signersToResend);
    setSending(false);
    setSelectedEmail({});
    setSelectedWhatsApp({});
  };

  const pendingSigners = signers
    .map((s, i) => ({ signer: s, globalIndex: i }))
    .filter(({ signer }) => !signer.hasSigned);

  const signedSigners = signers.filter((s) => s.hasSigned);

  return (
    <Modal
      isOpen={openModal.open}
      toggle={closeModal}
      className="modal-content modal-signer-links"
    >
      <div className="msl-header">
        <h2 className="msl-title">Signatários</h2>
        <span className="msl-subtitle">
          {pendingSigners.length} pendente(s) · {signedSigners.length} assinado(s)
        </span>
      </div>

      <ModalBody className="msl-body">
        {pendingSigners.length > 0 && (
          <div className="msl-section">
            <span className="msl-section-label pending">Pendentes</span>
            <span className="msl-section-hint">
              Marque o canal desejado para reenviar a notificação
            </span>

            {pendingSigners.map(({ signer, globalIndex }) => {
              const isEditingEmail = editing?.index === globalIndex && editing?.field === "email";
              const isEditingPhone = editing?.index === globalIndex && editing?.field === "phone";
              const emailEdited = globalIndex in editedEmails;
              const phoneEdited = globalIndex in editedPhones;
              const hasPhone = !!(signer.phone || editedPhones[globalIndex]);
              const isEmailSelected = !!selectedEmail[globalIndex];
              const isWhatsAppSelected = !!selectedWhatsApp[globalIndex];
              const isSelected = isEmailSelected || isWhatsAppSelected;

              return (
                <div key={`pending-${globalIndex}`} className={`msl-card ${isSelected ? "selected" : ""}`}>
                  <div className="msl-card-top">
                    <span className="msl-card-name">{signer.name}</span>
                    {signer.link && (
                      <button
                        className={`msl-copy-btn ${copiedIndex === globalIndex ? "copied" : ""}`}
                        onClick={() => copyToClipboard(signer.link!, globalIndex)}
                      >
                        <ContentCopyIcon style={{ fontSize: 14 }} />
                        {copiedIndex === globalIndex ? "Copiado!" : "Copiar Link"}
                      </button>
                    )}
                  </div>

                  {/* Email channel */}
                  <label className={`msl-channel ${isEmailSelected ? "active-email" : ""}`}>
                    <input
                      type="checkbox"
                      checked={isEmailSelected}
                      onChange={() => toggleEmail(globalIndex)}
                    />
                    <span className="msl-channel-label">E-mail</span>

                    {isEditingEmail ? (
                      <div className="msl-inline-edit" onClick={(e) => e.preventDefault()}>
                        <input
                          type="email"
                          className="msl-edit-input"
                          value={editingValue}
                          onChange={(e) => setEditingValue(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") confirmEdit();
                            if (e.key === "Escape") cancelEdit();
                          }}
                          placeholder="Novo e-mail"
                          autoFocus
                        />
                        <button className="msl-icon-btn confirm" onClick={(e) => { e.preventDefault(); confirmEdit(); }}>
                          <CheckIcon style={{ fontSize: 18 }} />
                        </button>
                        <button className="msl-icon-btn cancel" onClick={(e) => { e.preventDefault(); cancelEdit(); }}>
                          <CloseIcon style={{ fontSize: 18 }} />
                        </button>
                      </div>
                    ) : (
                      <div className="msl-channel-value">
                        <div className="msl-field-text">
                          <span className={`msl-field-value ${emailEdited ? "edited" : ""}`}>
                            {getEmail(globalIndex, signer)}
                          </span>
                          {emailEdited && (
                            <span className="msl-field-original">original: {signer.email}</span>
                          )}
                        </div>
                        <button
                          className="msl-icon-btn edit"
                          onClick={(e) => {
                            e.preventDefault();
                            startEditing(globalIndex, "email", getEmail(globalIndex, signer));
                          }}
                          title="Editar e-mail para reenvio"
                        >
                          <EditIcon style={{ fontSize: 15 }} />
                        </button>
                      </div>
                    )}
                  </label>

                  {/* WhatsApp channel */}
                  {hasPhone && (
                    <label className={`msl-channel ${isWhatsAppSelected ? "active-whatsapp" : ""}`}>
                      <input
                        type="checkbox"
                        checked={isWhatsAppSelected}
                        onChange={() => toggleWhatsApp(globalIndex)}
                      />
                      <span className="msl-channel-label">WhatsApp</span>

                      {isEditingPhone ? (
                        <div className="msl-inline-edit msl-phone-edit" onClick={(e) => e.preventDefault()}>
                          <input
                            type="text"
                            className="msl-country-code-input"
                            value={editingCountryCode}
                            onChange={(e) => {
                              let val = e.target.value;
                              if (!val.startsWith("+")) val = "+" + val;
                              setEditingCountryCode(val);
                            }}
                            maxLength={4}
                          />
                          <input
                            type="tel"
                            className="msl-edit-input"
                            placeholder="(00) 00000-0000"
                            value={formatPhoneDisplay(editingValue)}
                            onChange={(e) =>
                              setEditingValue(
                                e.target.value.replace(/\D/g, "").slice(0, 11)
                              )
                            }
                            onKeyDown={(e) => {
                              if (e.key === "Enter") confirmEdit();
                              if (e.key === "Escape") cancelEdit();
                            }}
                            autoFocus
                          />
                          <button className="msl-icon-btn confirm" onClick={(e) => { e.preventDefault(); confirmEdit(); }}>
                            <CheckIcon style={{ fontSize: 18 }} />
                          </button>
                          <button className="msl-icon-btn cancel" onClick={(e) => { e.preventDefault(); cancelEdit(); }}>
                            <CloseIcon style={{ fontSize: 18 }} />
                          </button>
                        </div>
                      ) : (
                        <div className="msl-channel-value">
                          <div className="msl-field-text">
                            <span className={`msl-field-value ${phoneEdited ? "edited" : ""}`}>
                              {getPhoneDisplay(globalIndex, signer)}
                            </span>
                            {phoneEdited && signer.phone && (
                              <span className="msl-field-original">original: {signer.phone}</span>
                            )}
                          </div>
                          <button
                            className="msl-icon-btn edit"
                            onClick={(e) => {
                              e.preventDefault();
                              startEditing(globalIndex, "phone", getPhone(globalIndex, signer));
                            }}
                            title="Editar telefone para reenvio"
                          >
                            <EditIcon style={{ fontSize: 15 }} />
                          </button>
                        </div>
                      )}
                    </label>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {signedSigners.length > 0 && (
          <div className="msl-section">
            <span className="msl-section-label signed">Assinados</span>
            {signedSigners.map((signer, index) => (
              <div key={`signed-${index}`} className="msl-card signed-card">
                <div className="msl-card-top">
                  <span className="msl-card-name">{signer.name}</span>
                  <span className="msl-badge signed">Assinado</span>
                </div>
                <span className="msl-signed-email">{signer.email}</span>
              </div>
            ))}
          </div>
        )}

        {signers.length === 0 && (
          <p className="msl-empty">Nenhum signatário encontrado</p>
        )}
      </ModalBody>

      <ModalFooter className="msl-footer">
        {pendingSigners.length > 0 && (
          <Button
            className="button-login msl-resend-btn"
            onClick={handleResend}
            disabled={sending || selectedCount === 0}
          >
            {sending ? "Enviando..." : `Reenviar (${selectedCount})`}
          </Button>
        )}
        <Button color="secondary" onClick={closeModal} className="msl-close-btn">
          Fechar
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default ModalSignerLinks;
